import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ProfileSettings = ({ userData, onUpdateUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: userData?.name || '',
    email: userData?.email || '',
    phone: userData?.phone || '',
    dateOfBirth: '1990-06-15',
    gender: 'female',
    bio: 'Beauty enthusiast and skincare lover. Always looking for the next amazing product to try!'
  });
  const [isUploading, setIsUploading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Simulate API call
    setTimeout(() => {
      onUpdateUser({
        ...userData,
        ...formData
      });
      setIsEditing(false);
    }, 1000);
  };

  // Hidden <input type="file"> we trigger from the avatar buttons. We read
  // the picked image as a data URL and bubble it up via onUpdateUser. A real
  // server-side upload endpoint is the next step (charms parity gap).
  const fileInputRef = React.useRef(null);
  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };
  const handleImageSelected = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      alert('Image is too large — please choose one under 4 MB.');
      return;
    }
    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      onUpdateUser?.({
        ...userData,
        profileImage: dataUrl,
        profileImageAlt: file.name || 'Profile photo',
      });
      setIsUploading(false);
      e.target.value = ''; // allow re-selecting the same file
    };
    reader.onerror = () => {
      setIsUploading(false);
      alert('Could not read that image. Try a different file.');
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="max-w-4xl">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageSelected}
      />
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Profile Settings
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Update your personal information and profile photo
              </p>
            </div>
            {!isEditing ? (
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                iconName="Edit"
                iconPosition="left"
              >
                Edit Profile
              </Button>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  iconName="Check"
                  iconPosition="left"
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile Photo Section */}
            <div className="lg:col-span-1">
              <div className="text-center">
                <div className="relative inline-block">
                  <img
                    src={userData?.profileImage}
                    alt={userData?.profileImageAlt}
                    className="w-32 h-32 rounded-full object-cover border-4 border-primary/10"
                  />
                  {isEditing && (
                    <button
                      onClick={handleImageUpload}
                      disabled={isUploading}
                      className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center text-white opacity-0 hover:opacity-100 transition-opacity"
                    >
                      {isUploading ? (
                        <div className="animate-spin">
                          <Icon name="Loader" size={24} />
                        </div>
                      ) : (
                        <Icon name="Camera" size={24} />
                      )}
                    </button>
                  )}
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-foreground">
                    {userData?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Member since {userData?.joinDate ? new Date(userData?.joinDate)?.toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    }) : '—'}
                  </p>
                </div>
                {isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={handleImageUpload}
                    loading={isUploading}
                    iconName="Upload"
                    iconPosition="left"
                  >
                    Change Photo
                  </Button>
                )}
              </div>
            </div>

            {/* Form Section */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-4">
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Full Name
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.name}
                          onChange={(e) => handleInputChange('name', e?.target?.value)}
                          placeholder="Enter your full name"
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground py-2">
                          {formData?.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Date of Birth
                      </label>
                      {isEditing ? (
                        <Input
                          type="date"
                          value={formData?.dateOfBirth}
                          onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground py-2">
                          {new Date(formData?.dateOfBirth)?.toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Gender
                      </label>
                      {isEditing ? (
                        <select
                          value={formData?.gender}
                          onChange={(e) => handleInputChange('gender', e?.target?.value)}
                          className="w-full px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        >
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                          <option value="other">Other</option>
                          <option value="prefer-not-to-say">Prefer not to say</option>
                        </select>
                      ) : (
                        <p className="text-sm text-muted-foreground py-2 capitalize">
                          {formData?.gender?.replace('-', ' ')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-4">
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Email Address
                      </label>
                      {isEditing ? (
                        <Input
                          type="email"
                          value={formData?.email}
                          onChange={(e) => handleInputChange('email', e?.target?.value)}
                          placeholder="Enter your email"
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground py-2">
                          {formData?.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Phone Number
                      </label>
                      {isEditing ? (
                        <Input
                          type="tel"
                          value={formData?.phone}
                          onChange={(e) => handleInputChange('phone', e?.target?.value)}
                          placeholder="Enter your phone number"
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground py-2">
                          {formData?.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bio Section */}
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-4">
                    Bio
                  </h4>
                  {isEditing ? (
                    <textarea
                      value={formData?.bio}
                      onChange={(e) => handleInputChange('bio', e?.target?.value)}
                      placeholder="Tell us about yourself..."
                      rows={4}
                      className="w-full px-3 py-2 border border-input rounded-md text-sm resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {formData?.bio}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;